import { supabase } from './supabase';
import { getUserBusinessId } from './supabaseDb';

type DbResult<T> = { data: T | null; error: string | null };

function handleError(err: any): string {
  if (!err) return 'Bilinmeyen hata';
  return err.message || err.details || String(err);
}

// ============================================================
// TYPES
// ============================================================
export interface PersonalAccount {
  id: string;
  business_id: string;
  account_no: string;
  account_name: string;
  person_type: string;
  phone: string | null;
  secondary_phone: string | null;
  address: string | null;
  description: string | null;
  notes: string | null;
  status: string;
  opened_at: string;
  closed_at: string | null;
  closure_reason: string | null;
  deleted_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface PersonalAccountMovement {
  id: string;
  business_id: string;
  personal_account_id: string;
  movement_no: string;
  movement_type: string;
  direction: string;
  asset_type: string;
  currency_code: string;
  asset_name: string | null;
  product_name: string | null;
  model_code: string | null;
  quantity: number;
  unit: string;
  unit_price: number | null;
  principal_amount: number;
  exchange_rate: number | null;
  gold_rate: number | null;
  tl_equivalent: number | null;
  movement_date: string;
  due_date: string | null;
  reason: string;
  description: string | null;
  status: string;
  cancelled_at: string | null;
  cancelled_by: string | null;
  cancellation_reason: string | null;
  created_by: string | null;
  created_at: string;
}

export interface AccountBalance {
  account_id: string;
  total_given: number;
  total_received: number;
  remaining: number;
  last_movement_date: string | null;
  overdue_amount: number;
}

export interface GlobalBalance {
  active_count: number;
  closed_count: number;
  total_given: number;
  total_received: number;
  total_remaining: number;
  total_overdue: number;
}

export interface MonthlySummary {
  month_given: number;
  month_received: number;
}

// ============================================================
// LABELS
// ============================================================
export const PERSON_TYPE_LABELS: Record<string, string> = {
  customer: 'Müşteri',
  employee: 'Personel',
  relative: 'Tanıdık',
  supplier: 'Tedarikçi',
  other: 'Diğer',
};

export const MOVEMENT_TYPE_LABELS: Record<string, string> = {
  para_verildi: 'Para Verildi',
  para_alindi: 'Para Alındı',
  urun_verildi: 'Ürün Verildi',
  altin_verildi: 'Altın Verildi',
  doviz_verildi: 'Döviz Verildi',
  urun_iade: 'Ürün İade',
  duzeltme: 'Düzeltme',
};

export const VERILME_NEDENI_LABELS: Record<string, string> = {
  gecici_borc: 'Geçici Borç',
  ticari_destek: 'Ticari Destek',
  avans: 'Avans',
  kisisel_kullanim: 'Kişisel Kullanım',
  diger: 'Diğer',
};

export const CURRENCY_LABELS: Record<string, { label: string; symbol: string; unit: string }> = {
  TRY: { label: 'TL', symbol: '₺', unit: 'TL' },
  USD: { label: 'Dolar', symbol: '$', unit: 'USD' },
  EUR: { label: 'Euro', symbol: '€', unit: 'EUR' },
  GBP: { label: 'Sterlin', symbol: '£', unit: 'GBP' },
  XAU: { label: 'Altın (Gram)', symbol: 'gr', unit: 'gram' },
};

export const CURRENCY_OPTIONS = ['TRY', 'USD', 'EUR', 'GBP'] as const;
export type CurrencyCode = (typeof CURRENCY_OPTIONS)[number];

export const GOLD_TYPE_LABELS: Record<string, { label: string; gramEquivalent: number }> = {
  gram: { label: 'Gram', gramEquivalent: 1 },
  ceyrek: { label: 'Çeyrek', gramEquivalent: 1.75 },
  yarim: { label: 'Yarım', gramEquivalent: 3.5 },
  tam: { label: 'Tam', gramEquivalent: 7 },
  cumhuriyet: { label: 'Cumhuriyet', gramEquivalent: 7.2 },
  bilezik: { label: 'Bilezik', gramEquivalent: 0 },
};

export const GOLD_TYPE_OPTIONS = ['gram', 'ceyrek', 'yarim', 'tam', 'cumhuriyet', 'bilezik'] as const;
export type GoldType = (typeof GOLD_TYPE_OPTIONS)[number];

// Movement filter categories for the detail page
export const MOVEMENT_FILTERS = [
  { key: 'all', label: 'Hepsi', icon: 'ri-list-unordered' },
  { key: 'para', label: 'Para', icon: 'ri-money-dollar-circle-line' },
  { key: 'urun', label: 'Ürün', icon: 'ri-archive-line' },
  { key: 'altin', label: 'Altın', icon: 'ri-copper-coin-line' },
  { key: 'doviz', label: 'Döviz', icon: 'ri-exchange-dollar-line' },
] as const;

// ============================================================
// ACCOUNTS
// ============================================================
export async function getPersonalAccounts(includeArchived = false): Promise<DbResult<PersonalAccount[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    let query = supabase
      .from('personal_accounts')
      .select('*')
      .eq('business_id', bizId);

    if (!includeArchived) {
      query = query.is('deleted_at', null);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as PersonalAccount[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function getPersonalAccountById(id: string): Promise<DbResult<PersonalAccount>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('personal_accounts')
      .select('*')
      .eq('id', id)
      .eq('business_id', bizId)
      .maybeSingle();

    if (error) return { data: null, error: handleError(error) };
    return { data: data as PersonalAccount, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function createPersonalAccount(payload: {
  account_name: string;
  person_type: string;
  phone: string;
  secondary_phone?: string | null;
  address?: string | null;
  description?: string | null;
  notes?: string | null;
  opened_at?: string | null;
  created_by?: string | null;
}): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('create_personal_account', {
      p_business_id: bizId,
      p_account_name: payload.account_name,
      p_person_type: payload.person_type || 'other',
      p_phone: payload.phone,
      p_secondary_phone: payload.secondary_phone || null,
      p_address: payload.address || null,
      p_description: payload.description || null,
      p_notes: payload.notes || null,
      p_created_by: payload.created_by || null,
      p_opened_at: payload.opened_at || null,
    });

    if (error) return { data: null, error: handleError(error) };

    const result = data as any;
    if (!result || result.success === false) {
      return { data: null, error: (result && result.error) || 'Hesap oluşturulamadı.' };
    }
    return { data: result, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function closePersonalAccount(id: string, closureReason?: string): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('close_personal_account', {
      p_business_id: bizId,
      p_account_id: id,
      p_closure_reason: closureReason || null,
    });

    if (error) return { data: null, error: handleError(error) };
    const result = data as any;
    if (!result || result.success === false) {
      return { data: null, error: (result && result.error) || 'Hesap kapatılamadı.' };
    }
    return { data: result, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function reopenPersonalAccount(id: string): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('reopen_personal_account', {
      p_business_id: bizId,
      p_account_id: id,
    });

    if (error) return { data: null, error: handleError(error) };
    const result = data as any;
    if (!result || result.success === false) {
      return { data: null, error: (result && result.error) || 'Hesap yeniden açılamadı.' };
    }
    return { data: result, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function archivePersonalAccount(id: string, deletionReason?: string): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('archive_personal_account', {
      p_business_id: bizId,
      p_account_id: id,
      p_deletion_reason: deletionReason || null,
    });

    if (error) return { data: null, error: handleError(error) };
    const result = data as any;
    if (!result || result.success === false) {
      return { data: null, error: (result && result.error) || 'Hesap silinemedi.' };
    }
    return { data: result, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// MOVEMENTS
// ============================================================
export async function getAccountMovements(accountId: string): Promise<DbResult<PersonalAccountMovement[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('personal_account_movements')
      .select('*')
      .eq('personal_account_id', accountId)
      .eq('business_id', bizId)
      .order('movement_date', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as PersonalAccountMovement[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function addMovement(payload: {
  personal_account_id: string;
  movement_type: string;
  direction: string;
  principal_amount: number;
  movement_date?: string;
  due_date?: string | null;
  reason?: string;
  description?: string | null;
  product_name?: string | null;
  model_code?: string | null;
  quantity?: number;
  unit?: string;
  unit_price?: number | null;
  created_by?: string | null;
  currency_code?: string;
  exchange_rate?: number | null;
  gold_rate?: number | null;
  tl_equivalent?: number | null;
  asset_name?: string | null;
}): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const isProduct = payload.movement_type === 'urun_verildi' || payload.movement_type === 'urun_iade';
    const isGold = payload.movement_type === 'altin_verildi';
    const isCurrency = payload.movement_type === 'doviz_verildi';
    const cc = payload.currency_code || 'TRY';

    let assetType = 'cash';
    if (isProduct) assetType = 'product';
    else if (isGold) assetType = 'gold';
    else if (isCurrency) assetType = 'foreign_currency';
    else if (cc === 'XAU') assetType = 'gold';

    const finalUnit = payload.unit || (isProduct ? 'adet' : (isGold ? 'gram' : CURRENCY_LABELS[cc]?.unit || 'TL'));
    const qty = payload.quantity || 1;
    const rateVal = cc === 'XAU' ? (payload.gold_rate || null) : (payload.exchange_rate || null);
    const tlEq = payload.tl_equivalent || payload.principal_amount;

    const { data, error } = await supabase.rpc('add_personal_account_movement', {
      p_business_id: bizId,
      p_personal_account_id: payload.personal_account_id,
      p_movement_type: payload.movement_type,
      p_direction: payload.direction,
      p_asset_type: assetType,
      p_currency_code: cc,
      p_asset_name: payload.asset_name || payload.product_name || null,
      p_product_id: null,
      p_quantity: qty,
      p_unit: finalUnit,
      p_unit_price: payload.unit_price || null,
      p_principal_amount: payload.principal_amount,
      p_exchange_rate: rateVal,
      p_gold_rate: cc === 'XAU' ? (payload.gold_rate || null) : null,
      p_tl_equivalent: tlEq,
      p_movement_date: payload.movement_date || new Date().toISOString().slice(0, 10),
      p_due_date: payload.due_date || null,
      p_reason: payload.reason || 'diger',
      p_description: payload.description || null,
      p_document_no: null,
      p_cash_account_id: null,
      p_created_by: payload.created_by || null,
      p_deduct_stock: false,
    });

    if (error) return { data: null, error: handleError(error) };
    const result = data as any;
    if (!result || result.success === false) {
      return { data: null, error: (result && result.error) || 'Hareket eklenemedi.' };
    }
    return { data: result, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function cancelMovement(movementId: string, cancellationReason: string): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('cancel_personal_account_movement', {
      p_business_id: bizId,
      p_movement_id: movementId,
      p_cancellation_reason: cancellationReason,
    });

    if (error) return { data: null, error: handleError(error) };
    const result = data as any;
    if (!result || result.success === false) {
      return { data: null, error: (result && result.error) || 'Hareket iptal edilemedi.' };
    }
    return { data: result, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// BALANCE
// ============================================================
export async function getAccountBalance(accountId?: string): Promise<DbResult<AccountBalance | GlobalBalance>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('get_personal_account_balance', {
      p_business_id: bizId,
      p_account_id: accountId || null,
    });

    if (error) return { data: null, error: handleError(error) };
    return { data: data as any, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// MONTHLY SUMMARY (for main page cards)
// ============================================================
export async function getMonthlySummary(): Promise<DbResult<MonthlySummary>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);

    const { data, error } = await supabase
      .from('personal_account_movements')
      .select('direction, principal_amount, cancelled_at')
      .eq('business_id', bizId)
      .gte('movement_date', firstDay)
      .lte('movement_date', lastDay)
      .is('cancelled_at', null);

    if (error) return { data: null, error: handleError(error) };

    let monthGiven = 0;
    let monthReceived = 0;
    for (const m of (data || [])) {
      if (m.direction === 'receivable') monthGiven += Number(m.principal_amount) || 0;
      else monthReceived += Number(m.principal_amount) || 0;
    }

    return { data: { month_given: monthGiven, month_received: monthReceived }, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}